import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  setDoc,
  Timestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';

import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { firestore, storage } from '../firebase';
import Org from '../types/Org';
import { OrgVanityUrlRequest, ORG_VANITY_URL_REQUEST_STATUSES, PAGE_STATUSES } from '../types/Page';
import { validatePublicLink } from '../utils/validators';

const LOGO_FETCH_TIMEOUT_MS = 5000;

export const useOrgVanityUrlRequestMutators = () => {
  const { isGod } = useAuth();
  const { userState } = useData();

  const requestVanityUrl = async (org: Org, vanityUrl: string): Promise<string> => {
    if (userState.status !== 'ready' || !userState.userProfile) {
      throw new Error('User profile not ready');
    }

    if (!validatePublicLink(vanityUrl)) {
      throw new Error('Invalid vanity URL format');
    }

    if (!org.itemId || org.itemId.length > 50) {
      throw new Error('Invalid organization');
    }

    if (!org.name || org.name.length > 200) {
      throw new Error('Organization name is missing or too long');
    }

    // Pre-check availability
    const existingDoc = await getDoc(doc(firestore, `vanityUrlLookup/${vanityUrl}`));
    if (existingDoc.exists()) {
      throw new Error('This URL is already taken');
    }

    const docRef = doc(collection(firestore, 'orgVanityUrlRequest'));
    await setDoc(docRef, {
      itemId: docRef.id,
      createTime: Timestamp.now(),
      creatorId: userState.userProfile.itemId,
      orgId: org.itemId,
      orgName: org.name,
      requestedVanityUrl: vanityUrl,
      status: ORG_VANITY_URL_REQUEST_STATUSES.PENDING,
    });

    return docRef.id;

  };

  const approveVanityUrl = async (request: OrgVanityUrlRequest, org: Org): Promise<void> => {
    if (!isGod) throw new Error('Permission denied');
    if (userState.status !== 'ready' || !userState.userProfile) {
      throw new Error('User profile not ready');
    }

    // Fetch logo download URL if logo exists (with timeout to avoid hanging)
    let logoUrl = '';
    if (org.logo) {
      try {
        const logoRef = ref(storage, `org/${org.itemId}/publicFile/${org.logo}`);
        const logoPromise = getDownloadURL(logoRef);
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Logo fetch timed out')), LOGO_FETCH_TIMEOUT_MS)
        );
        logoUrl = await Promise.race([logoPromise, timeoutPromise]);
      } catch {
        // Logo fetch failed — proceed without it
      }
    }

    // Guard against approving a vanity URL already claimed by another org
    const existingPageName = await getDoc(doc(firestore, `vanityUrlLookup/${request.requestedVanityUrl}`));
    if (existingPageName.exists() && (existingPageName.data() as { orgId: string }).orgId !== org.itemId) {
      throw new Error(`The link "${request.requestedVanityUrl}" is already taken by another org`);
    }

    const db = firestore;
    const batch = writeBatch(db);

    // Set publicPage document (default empty strings for potentially missing org fields)
    const publicPageRef = doc(db, `publicPage/${org.itemId}`);
    batch.set(publicPageRef, {
      itemId: org.itemId,
      createTime: Timestamp.now(),
      orgId: org.itemId,
      pageName: request.requestedVanityUrl,
      name: org.name || '',
      shortName: org.shortName || '',
      description: org.description || '',
      logo: org.logo || '',
      logoUrl,
      homeUrl: org.homeUrl || '',
      status: PAGE_STATUSES.ACTIVE,
    });

    // Set vanityUrlLookup lookup document
    const vanityUrlLookupRef = doc(db, `vanityUrlLookup/${request.requestedVanityUrl}`);
    batch.set(vanityUrlLookupRef, { orgId: org.itemId });

    // Update the request status
    const requestRef = doc(db, `orgVanityUrlRequest/${request.itemId}`);
    batch.update(requestRef, {
      status: ORG_VANITY_URL_REQUEST_STATUSES.APPROVED,
      resolvedAt: Timestamp.now(),
      resolvedBy: userState.userProfile.itemId,
    });

    // Update the org vanity URL status directly (don't rely solely on Cloud Function)
    const orgRef = doc(db, `org/${org.itemId}`);
    batch.update(orgRef, {
      vanityUrlStatus: 'ACTIVE',
    });

    await batch.commit();

    // Verify the write persisted (persistent cache can resolve before server confirms)
    const verifyDoc = await getDocFromServer(requestRef);
    if (!verifyDoc.exists() || verifyDoc.data()?.status !== ORG_VANITY_URL_REQUEST_STATUSES.APPROVED) {
      throw new Error('Approval failed to persist — the write may have been rejected by security rules');
    }
  };

  const rejectVanityUrl = async (request: OrgVanityUrlRequest, reason: string): Promise<void> => {
    if (!isGod) throw new Error('Permission denied');
    if (userState.status !== 'ready' || !userState.userProfile) {
      throw new Error('User profile not ready');
    }

    await updateDoc(doc(firestore, `orgVanityUrlRequest/${request.itemId}`), {
      status: ORG_VANITY_URL_REQUEST_STATUSES.REJECTED,
      rejectionReason: reason,
      resolvedAt: Timestamp.now(),
      resolvedBy: userState.userProfile.itemId,
    });

  };

  const cancelVanityUrl = async (requestId: string): Promise<void> => {
    if (userState.status !== 'ready' || !userState.userProfile) {
      throw new Error('User profile not ready');
    }

    await updateDoc(doc(firestore, `orgVanityUrlRequest/${requestId}`), {
      status: ORG_VANITY_URL_REQUEST_STATUSES.CANCELLED,
    });
  };

  return { requestVanityUrl, approveVanityUrl, rejectVanityUrl, cancelVanityUrl };
};
